import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Clear as ClearIcon,
  Menu as MenuIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Hidden,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { toggleNavCollapsed } from "../../redux/actions";
import { getRisOrgs, searchRisUsers } from "../../redux/actions/RisAdmin";
import { fontColor } from "../../constants/ColorSets";
import {
  getAccessLevelFromToken,
  getAuthUserInfoFromToken,
  isCurrentAccountOrgManagerOrAdmin,
} from "../../components/AppBase/AppAuthProvider";
import AppLoader from "../../components/AppBase/AppLoader";
import AppInfoView from "../../components/AppBase/AppInfoView";
import EmptyResult from "../rules/EmptyResult";
import { formatExpireDate } from "../../utils/StringUtils";
import { RisOrg } from "../../types/models/RisOrg";
import { RisOrgUser, RisUserSearchResponse } from "../../types/models/RisOrgUser";
import AddUserDialog from "./AddUserDialog";
import EditUserDialog from "./EditUserDialog";
import { Caller, accessLevelLabels, toAssignableAccessLevel } from "./permissions";

const ALL_ORGS = "all";
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const displayName = (user: RisOrgUser) =>
  [user.FirstName, user.LastName].filter(Boolean).join(" ") || user.Email;

/**
 * Users in RIS orgs, for Admins (every org) and Org Managers (their own org). Replaces the user
 * management side of the RIS Power Apps portal.
 */
const OrgUsersPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const caller: Caller = {
    accessLevel: getAccessLevelFromToken(),
    email: getAuthUserInfoFromToken()?.Email ?? "",
  };
  const isAdmin = caller.accessLevel === "admin";

  const [orgs, setOrgs] = useState<RisOrg[]>([]);
  const [orgsLoaded, setOrgsLoaded] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(ALL_ORGS);
  const [searchText, setSearchText] = useState<string>("");
  const [appliedSearch, setAppliedSearch] = useState<string>("");
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[1]);
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<RisUserSearchResponse>();
  const [editUser, setEditUser] = useState<RisOrgUser>();
  const [openAddUser, setOpenAddUser] = useState<boolean>(false);

  useEffect(() => {
    if (!isCurrentAccountOrgManagerOrAdmin()) {
      navigate(-1);
      return;
    }
    dispatch(
      getRisOrgs((result) => {
        const loadedOrgs = result ?? [];
        setOrgs(loadedOrgs);
        // Org managers only get their own org back
        if (!isAdmin && loadedOrgs.length === 1) {
          setSelectedOrgId(loadedOrgs[0].Id);
        }
        setOrgsLoaded(true);
      })
    );
  }, []);

  const loadUsers = () => {
    setLoading(true);
    dispatch(
      searchRisUsers(
        {
          OrgId: selectedOrgId === ALL_ORGS ? undefined : selectedOrgId,
          Q: appliedSearch || undefined,
          IncludeInactive: includeInactive || undefined,
          Page: page + 1,
          PageSize: pageSize,
        },
        (response) => {
          setResult(response);
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    if (orgsLoaded) {
      loadUsers();
    }
  }, [orgsLoaded, selectedOrgId, appliedSearch, includeInactive, page, pageSize]);

  const applySearch = (value: string) => {
    setPage(0);
    setAppliedSearch(value.trim());
  };

  const handleUserChanged = () => {
    setEditUser(undefined);
    setOpenAddUser(false);
    // Refresh, since the user may no longer match the current filters
    loadUsers();
  };

  const users = result?.Users ?? [];
  const selectedOrg = orgs.find((org) => org.Id === selectedOrgId);

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "95%",
            height: "95%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box sx={{ backgroundColor: theme.page.bgColor, minHeight: "100vh" }}>
        <Box sx={{ width: "100%", p: 2, pb: 0 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minHeight: "60px", display: "flex", alignItems: "center" }}>
              <Hidden lgUp>
                <IconButton
                  sx={{ color: "text.secondary", marginLeft: 1, padding: 0 }}
                  edge="start"
                  className="menu-btn"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={() => dispatch(toggleNavCollapsed())}
                  size="large"
                >
                  <MenuIcon sx={{ width: 35, height: 35 }} />
                </IconButton>
              </Hidden>
              <Box sx={{ pt: 1, display: "flex", alignItems: "baseline", gap: 2 }}>
                <Typography
                  variant="h3"
                  component="h3"
                  color={fontColor.titleFontColor}
                  style={{ fontSize: 22, marginLeft: 12 }}
                >
                  Users
                </Typography>
                {!isAdmin && selectedOrg && (
                  <Typography sx={{ fontSize: 14, color: theme.table.subTextColor }}>
                    <strong>Organisation:</strong>&nbsp;{selectedOrg.Name}
                  </Typography>
                )}
              </Box>
              <Button
                startIcon={<PersonAddIcon />}
                variant="contained"
                disabled={orgs.length === 0}
                onClick={() => setOpenAddUser(true)}
                sx={{ ml: 3, textTransform: "none", borderRadius: 1, color: "white" }}
              >
                Add User
              </Button>
            </Box>
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                applySearch(searchText);
              }}
              sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap", mr: 2 }}
            >
              {isAdmin && (
                <FormControl variant="standard" sx={{ minWidth: 200 }}>
                  <InputLabel id="org-filter-label">Organisation</InputLabel>
                  <Select
                    labelId="org-filter-label"
                    value={selectedOrgId}
                    onChange={(event) => {
                      setPage(0);
                      setSelectedOrgId(event.target.value);
                    }}
                  >
                    <MenuItem value={ALL_ORGS}>All organisations</MenuItem>
                    {orgs.map((org) => (
                      <MenuItem key={org.Id} value={org.Id}>
                        {org.Name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                id="search"
                label="Search (first name, last name or email)"
                variant="standard"
                value={searchText}
                sx={{ width: 300 }}
                onChange={(event) => setSearchText(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          aria-label="clear search"
                          onClick={() => {
                            setSearchText("");
                            applySearch("");
                          }}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton aria-label="search" type="submit" edge="end" size="small">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeInactive}
                    onChange={(event) => {
                      setPage(0);
                      setIncludeInactive(event.target.checked);
                    }}
                  />
                }
                label={<Typography variant="body2">Show deactivated</Typography>}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderBottom: "1px solid lightgrey", mx: 1.5, mt: 1 }}></Box>

        <Box sx={{ p: 2 }}>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {isAdmin && <TableCell>Organisation</TableCell>}
                  <TableCell>Access level</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{ "& .MuiTableRow-root:hover": { backgroundColor: theme.table.hoverBgColor } }}
              >
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell sx={{ p: 0 }} colSpan={isAdmin ? 6 : 5}>
                      <EmptyResult>{loading ? "" : "No users found"}</EmptyResult>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.Id}
                      hover
                      onClick={() => setEditUser(user)}
                      sx={{ cursor: "pointer", opacity: user.Active ? 1 : 0.6 }}
                    >
                      <TableCell>{displayName(user)}</TableCell>
                      <TableCell>{user.Email}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          {user.OrgName ?? (
                            <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                              No organisation
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {accessLevelLabels[toAssignableAccessLevel(user.AccessLevel)]}
                      </TableCell>
                      <TableCell>
                        {user.ExpiryDate ? formatExpireDate(user.ExpiryDate) : "—"}
                      </TableCell>
                      <TableCell>
                        {!user.Active ? (
                          <Chip size="small" label="Deactivated" />
                        ) : user.Pending ? (
                          <Chip size="small" color="warning" variant="outlined" label="Pending" />
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label="Active" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              count={result?.TotalCount ?? 0}
              page={result ? page : 0}
              rowsPerPage={pageSize}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setPage(0);
                setPageSize(parseInt(event.target.value, 10));
              }}
            />
          </TableContainer>
        </Box>
        <Box sx={{ height: 100 }} />
      </Box>
      <AddUserDialog
        isOpen={openAddUser}
        orgs={orgs}
        orgId={selectedOrgId === ALL_ORGS ? undefined : selectedOrgId}
        onClose={() => setOpenAddUser(false)}
        onAdded={handleUserChanged}
      />
      <EditUserDialog
        isOpen={!!editUser}
        user={editUser}
        caller={caller}
        orgs={orgs}
        onClose={() => setEditUser(undefined)}
        onChanged={handleUserChanged}
      />
      <AppInfoView />
    </>
  );
};

export default OrgUsersPage;
